import { notifyError } from "@/lib/notify-error";
import { useEffect, useRef, useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { convexQuery } from "@convex-dev/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { FileText, Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import type { Resolver } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { getRevPilotComparisonKind } from "@constructionautomation/backend/convex/lib/revisionDocumentTypes";
import type { ComparisonKind } from "@constructionautomation/backend/convex/lib/scheduleComparisonCreation";
import { FormActions } from "@/components/layout";
import { useProject } from "@/components/projects";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ProjectDocumentPicker,
  type PickerDocument,
  type PickerRevision,
  type PickerSection,
  type SectionSelections,
} from "@/components/project-document-picker";
import { useAppNavigate } from "@/lib/use-app-navigate";

const formSchema = z.object({
  name: z.string().min(1, "Comparison name is required"),
});

type FormValues = z.infer<typeof formSchema>;

interface RevPilotComparisonFormProps {
  onSuccess: (comparisonId: Id<"revPilotScheduleComparisons">) => void;
  cancelTo?: "/" | "/comparisons" | "/projects" | "/revPilot" | "/revPilot/new";
  initialV1TakeoffJobId?: Id<"takeoffPilotJobs">;
}

interface MultiPairSelection {
  v1RevisionId: string;
  v2RevisionId: string;
}

// ---------------------------------------------------------------------------
// RevPilot-specific revision filtering
// ---------------------------------------------------------------------------

function getRevPilotV2Revisions(
  document: PickerDocument,
  v1RevisionId: string
): PickerRevision[] {
  const v1 = document.revisions.find((r) => r._id === v1RevisionId);
  const v1Kind = v1 && getRevPilotComparisonKind(v1);
  if (!v1 || !v1Kind) return [];
  return document.revisions
    .filter(
      (r) =>
        getRevPilotComparisonKind(r) === v1Kind &&
        r.revisionNumber > v1.revisionNumber
    )
    .sort(
      (a, b) =>
        a.revisionNumber - b.revisionNumber || a._id.localeCompare(b._id)
    );
}

function getNewerTakeoffRevisions<T extends { revisionNumber: number; isCurrent: boolean }>(plan: {
  currentRevisionNumber: number;
  revisions: T[];
}) {
  return plan.revisions.filter(
    (r) => !r.isCurrent && r.revisionNumber > plan.currentRevisionNumber
  );
}

function detectComparisonKind(
  document: PickerDocument,
  v1RevisionId: string,
  v2RevisionId: string
): ComparisonKind {
  const v1 = document.revisions.find((r) => r._id === v1RevisionId);
  const v2 = document.revisions.find((r) => r._id === v2RevisionId);
  if (!v1 || !v2) throw new Error("Select both v1 and v2 revisions");
  const v1Kind = getRevPilotComparisonKind(v1);
  const v2Kind = getRevPilotComparisonKind(v2);
  if (!v1Kind || !v2Kind) {
    throw new Error(
      "Selected revisions must be classified as schedules or plan sets"
    );
  }
  if (v1Kind !== v2Kind) {
    throw new Error(
      "v1 and v2 revisions must have the same document type"
    );
  }
  if (v2.revisionNumber <= v1.revisionNumber) {
    throw new Error("v2 revision must be newer than v1 revision");
  }
  return v1Kind;
}

// ---------------------------------------------------------------------------
// Main form
// ---------------------------------------------------------------------------

export function RevPilotComparisonForm({
  onSuccess,
  cancelTo = "/revPilot",
  initialV1TakeoffJobId,
}: RevPilotComparisonFormProps) {
  const appNavigate = useAppNavigate();
  const { projectId, project } = useProject();
  const createComparisonFromRevisions = useMutation(
    api.scheduleComparisons.createFromProjectRevisionsWithOperations
  );
  const createMultiDocumentReview = useMutation(
    api.revPilotDocumentPairs.createMultiDocumentReview
  );

  // Standalone picker state
  const [pickerValues, setPickerValues] = useState<
    Record<string, SectionSelections>
  >({ documents: { mode: "multi-pair", selections: [] } });

  // Takeoff-linked state
  const [v1TakeoffJobId, setV1TakeoffJobId] = useState(
    initialV1TakeoffJobId ? String(initialV1TakeoffJobId) : ""
  );
  const [takeoffSelections, setTakeoffSelections] = useState<
    Record<string, MultiPairSelection>
  >({});
  const deselectedTakeoffDocumentKeys = useRef<Set<string>>(new Set());

  const [libraryError, setLibraryError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formResolver = zodResolver(
    formSchema as unknown as Parameters<typeof zodResolver>[0]
  ) as unknown as Resolver<FormValues>;

  const form = useForm<FormValues>({
    resolver: formResolver,
    defaultValues: { name: "" },
  });

  // Queries
  const { data: rawCompareOptions } = useQuery(
    convexQuery(
      api.projectDocumentRevisionOptions.listPdfCompareOptions,
      projectId ? { projectId } : "skip"
    )
  );
  const { data: takeoffJobs } = useQuery(
    convexQuery(api.takeoffPilot.jobs.listAll, {})
  );
  const takeoffOptionsQuery = useQuery(
    convexQuery(
      api.takeoffPilot.jobRevisions.getRevisionOptionsForJob,
      v1TakeoffJobId
        ? { jobId: v1TakeoffJobId as Id<"takeoffPilotJobs"> }
        : "skip"
    )
  );
  const takeoffOptions = takeoffOptionsQuery.data;

  // Build picker documents from compare options
  const pickerDocuments: PickerDocument[] = (rawCompareOptions ?? []).map(
    (doc) => ({
      _id: String(doc._id),
      name: doc.name,
      revisions: doc.revisions
        .filter((r) => getRevPilotComparisonKind(r) !== undefined)
        .map((r) => ({
          _id: String(r._id),
          revisionNumber: r.revisionNumber,
          revisionName: r.revisionName,
          effectiveDocumentType: r.effectiveDocumentType,
          pageDocumentTypes: r.pageDocumentTypes,
          classificationStatus: r.classificationStatus,
        })),
    })
  ).filter((doc) => doc.revisions.length > 0);

  const sections: PickerSection[] = [
    {
      key: "documents",
      label: "Documents",
      documents: pickerDocuments,
      selectionMode: "multi",
      revisionMode: "pair",
      getAvailableV2Revisions: getRevPilotV2Revisions,
      addLabel: "Add Document",
    },
  ];

  // Effects
  useEffect(() => {
    if (initialV1TakeoffJobId) {
      setV1TakeoffJobId(String(initialV1TakeoffJobId));
    }
  }, [initialV1TakeoffJobId]);

  useEffect(() => {
    if (!takeoffOptions || !v1TakeoffJobId) return;
    const currentName = form.getValues("name").trim();
    if (!currentName) {
      form.setValue(
        "name",
        `${takeoffOptions.sourceJob.name} RevPilot R${takeoffOptions.nextRevisionNumber}`
      );
    }
  }, [form, takeoffOptions, v1TakeoffJobId]);

  useEffect(() => {
    setPickerValues({
      documents: { mode: "multi-pair", selections: [] },
    });
    setTakeoffSelections({});
    deselectedTakeoffDocumentKeys.current = new Set();
    setLibraryError(undefined);
  }, [projectId]);

  useEffect(() => {
    if (!takeoffOptions || !v1TakeoffJobId) return;

    setTakeoffSelections((current) => {
      const nextSelections: Record<string, MultiPairSelection> = {};
      for (const plan of takeoffOptions.planDocuments) {
        const key = String(plan.sourcePlanDocumentId);
        const changedRevisions = getNewerTakeoffRevisions(plan);
        const latest = changedRevisions.at(-1);
        if (!latest || deselectedTakeoffDocumentKeys.current.has(key)) continue;
        const existing = current[key];
        const existingV2IsAvailable = changedRevisions.some(
          (revision) => String(revision._id) === existing?.v2RevisionId
        );
        nextSelections[key] =
          existing?.v1RevisionId === String(plan.currentRevisionId) && existingV2IsAvailable
            ? existing
            : {
                v1RevisionId: String(plan.currentRevisionId),
                v2RevisionId: String(latest._id),
              };
      }
      return nextSelections;
    });
  }, [takeoffOptions, v1TakeoffJobId]);

  const setSourceTakeoffJob = (value: string) => {
    setV1TakeoffJobId(value === "standalone" ? "" : value);
    setTakeoffSelections({});
    deselectedTakeoffDocumentKeys.current = new Set();
    setLibraryError(undefined);
  };

  const handlePickerChange = useCallback(
    (sectionKey: string, value: SectionSelections) => {
      setPickerValues((prev) => ({ ...prev, [sectionKey]: value }));
      setLibraryError(undefined);
    },
    []
  );

  // Submission handlers
  const createStandaloneReview = async (
    values: FormValues
  ): Promise<Id<"revPilotScheduleComparisons">> => {
    if (!projectId) throw new Error("No project selected");

    const pairSels =
      pickerValues.documents?.mode === "multi-pair"
        ? pickerValues.documents.selections
        : [];

    if (pairSels.length === 0) {
      setLibraryError("Select at least one document with v1 and v2 revisions");
      throw new Error("Invalid revision selection");
    }
    if (pairSels.some((s) => !s.documentId || !s.v1RevisionId || !s.v2RevisionId)) {
      setLibraryError("Choose both v1 and v2 for every selected document");
      throw new Error("Invalid revision selection");
    }

    const pairs = pairSels.map((sel, index) => {
      const doc = pickerDocuments.find((d) => d._id === sel.documentId);
      if (!doc) throw new Error("Selected document not found");
      const kind = detectComparisonKind(
        doc,
        sel.v1RevisionId,
        sel.v2RevisionId
      );
      return {
        doc,
        comparisonKind: kind,
        projectDocumentId: sel.documentId as Id<"projectDocuments">,
        v1RevisionId: sel.v1RevisionId as Id<"projectDocumentRevisions">,
        v2RevisionId: sel.v2RevisionId as Id<"projectDocumentRevisions">,
        displayName: doc.name,
        documentOrder: index,
      };
    });

    setLibraryError(undefined);

    if (pairs.length === 1) {
      const pair = pairs[0]!;
      return await createComparisonFromRevisions({
        projectId,
        name: values.name,
        comparisonKind: pair.comparisonKind,
        v1RevisionId: pair.v1RevisionId,
        v2RevisionId: pair.v2RevisionId,
        allowAutoSwapIfReversed: false,
      });
    }

    if (pairs.some((p) => p.comparisonKind !== "plan_set")) {
      throw new Error(
        "Multi-document RevPilot reviews currently support plan-set documents only"
      );
    }

    return await createMultiDocumentReview({
      projectId,
      name: values.name,
      pairs: pairs.map(
        ({
          projectDocumentId,
          v1RevisionId,
          v2RevisionId,
          displayName,
          documentOrder,
        }) => ({
          projectDocumentId,
          v1RevisionId,
          v2RevisionId,
          displayName,
          documentOrder,
        })
      ),
    });
  };

  const createTakeoffLinkedReview = async (
    values: FormValues
  ): Promise<Id<"revPilotScheduleComparisons">> => {
    if (!projectId) throw new Error("No project selected");
    if (!takeoffOptions) {
      setLibraryError("Wait for Takeoff revision options to load");
      throw new Error("Invalid revision selection");
    }

    const entries = Object.entries(takeoffSelections).filter(
      ([, sel]) => sel.v1RevisionId && sel.v2RevisionId
    );
    if (entries.length === 0) {
      setLibraryError("Select at least one changed Takeoff document");
      throw new Error("Invalid revision selection");
    }

    const pairs = entries.map(([key, sel]) => {
      const plan = takeoffOptions.planDocuments.find(
        (p) => String(p.sourcePlanDocumentId) === key
      );
      if (!plan)
        throw new Error(
          "Selected Takeoff plan document is no longer available"
        );
      return {
        projectDocumentId: plan.projectDocumentId,
        sourceTakeoffPlanDocumentId: plan.sourcePlanDocumentId,
        v1RevisionId: sel.v1RevisionId as Id<"projectDocumentRevisions">,
        v2RevisionId: sel.v2RevisionId as Id<"projectDocumentRevisions">,
        displayName: plan.documentName,
        documentOrder: plan.documentOrder,
      };
    });

    setLibraryError(undefined);
    return await createMultiDocumentReview({
      projectId,
      name: values.name,
      v1TakeoffJobId: v1TakeoffJobId as Id<"takeoffPilotJobs">,
      pairs,
    });
  };

  const onSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    try {
      const comparisonId = v1TakeoffJobId
        ? await createTakeoffLinkedReview(values)
        : await createStandaloneReview(values);
      toast.success("RevPilot review started");
      onSuccess(comparisonId);
    } catch (error) {
      console.error("Comparison creation error:", error);
      const message =
        error instanceof Error ? error.message : "Failed to start comparison";
      if (message !== "Invalid revision selection") {
        setLibraryError(message);
        notifyError(error, message);
      }
      setIsSubmitting(false);
      return;
    }
    setIsSubmitting(false);
  };

  const hasSelectedDocuments = v1TakeoffJobId
    ? Object.values(takeoffSelections).some(
        (selection) => selection.v1RevisionId && selection.v2RevisionId
      )
    : pickerValues.documents?.mode === "multi-pair" &&
      pickerValues.documents.selections.some(
        (selection) =>
          selection.documentId && selection.v1RevisionId && selection.v2RevisionId
      );

  const submitDisabled =
    isSubmitting || !hasSelectedDocuments || Boolean(v1TakeoffJobId && !takeoffOptions);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="rounded-lg border bg-muted/50 p-4 shadow-sm">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">
              Project:
            </span>
            <span className="text-sm font-semibold">
              {project ? project.name : "No project selected"}
            </span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Build one RevPilot review from one or more document revision pairs
            in this project.
          </p>
        </div>

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Comparison Name</FormLabel>
              <FormControl>
                <Input
                  placeholder="e.g. Mechanical Plan Set Rev 3 vs Rev 4"
                  {...field}
                  disabled={isSubmitting}
                />
              </FormControl>
              <FormDescription>
                Used for report naming and listing in RevPilot history.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-2 rounded-lg border-2 bg-card p-4 shadow-sm">
          <FormLabel htmlFor="revpilot-takeoff-source">
            New or Source Takeoff Job
          </FormLabel>
          <Select
            value={v1TakeoffJobId || "standalone"}
            onValueChange={setSourceTakeoffJob}
            disabled={isSubmitting}
          >
            <SelectTrigger id="revpilot-takeoff-source">
              <SelectValue placeholder="New RevPilot review" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="standalone">
                New RevPilot review
              </SelectItem>
              {(takeoffJobs ?? [])
                .filter((job) => !projectId || job.projectId === projectId)
                .map((job) => (
                  <SelectItem key={job._id} value={String(job._id)}>
                    {job.name} (R{job.revisionNumber ?? 1})
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Choose New for a standalone review, or select a v1 Takeoff job to link Takeoff context now.
          </p>
        </div>

        {v1TakeoffJobId ? (
          <TakeoffLinkedDocuments
            takeoffOptions={takeoffOptions}
            takeoffSelections={takeoffSelections}
            isSubmitting={isSubmitting}
            deselectedDocumentKeys={deselectedTakeoffDocumentKeys}
            onSelectionsChange={setTakeoffSelections}
            onClearError={() => setLibraryError(undefined)}
          />
        ) : (
          <ProjectDocumentPicker
            sections={sections}
            values={pickerValues}
            onChange={handlePickerChange}
            isLoading={rawCompareOptions === undefined}
            disabled={isSubmitting}
          />
        )}

        {libraryError ? (
          <p className="text-sm font-medium text-destructive">
            {libraryError}
          </p>
        ) : null}

        <FormActions
          onCancel={() => appNavigate({ to: cancelTo })}
          submitLabel={isSubmitting ? "Starting..." : "Start RevPilot Review"}
          submitting={isSubmitting}
          submitDisabled={submitDisabled}
        />
      </form>
    </Form>
  );
}

// ---------------------------------------------------------------------------
// Takeoff-linked document section (unchanged behavior, consistent card style)
// ---------------------------------------------------------------------------

function TakeoffLinkedDocuments({
  takeoffOptions,
  takeoffSelections,
  isSubmitting,
  deselectedDocumentKeys,
  onSelectionsChange,
  onClearError,
}: {
  takeoffOptions: {
    sourceJob: { _id: string; name: string };
    nextRevisionNumber: number;
    planDocuments: Array<{
      sourcePlanDocumentId: Id<"takeoffPilotPlanDocuments">;
      projectDocumentId: Id<"projectDocuments">;
      documentName: string;
      documentOrder: number;
      currentRevisionId: Id<"projectDocumentRevisions">;
      currentRevisionName: string;
      currentRevisionNumber: number;
      revisions: Array<{
        _id: Id<"projectDocumentRevisions">;
        revisionNumber: number;
        revisionName: string;
        originalFilename: string;
        isCurrent: boolean;
      }>;
    }>;
  } | undefined;
  takeoffSelections: Record<string, MultiPairSelection>;
  isSubmitting: boolean;
  deselectedDocumentKeys: React.MutableRefObject<Set<string>>;
  onSelectionsChange: React.Dispatch<
    React.SetStateAction<Record<string, MultiPairSelection>>
  >;
  onClearError: () => void;
}) {
  return (
    <section className="space-y-4 rounded-lg border-2 bg-card p-4 shadow-sm">
      <div>
        <h3 className="text-sm font-semibold leading-none tracking-tight">
          Source Documents
        </h3>
        <p className="mt-1 text-xs text-muted-foreground">
          Select documents and revisions from your project library.
        </p>
      </div>

      <div className="rounded-lg border bg-muted/30 p-3">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm font-semibold">
              {takeoffOptions
                ? takeoffOptions.sourceJob.name
                : "Loading Takeoff documents..."}
            </p>
            <p className="text-xs text-muted-foreground">
              Changed documents are reviewed by RevPilot. Unchanged documents
              are carried forward.
            </p>
          </div>
        </div>
      </div>

      {!takeoffOptions ? (
        <div className="flex items-center justify-center rounded-lg border py-8 text-sm text-muted-foreground">
          <Loader2 className="me-2 size-4 animate-spin" />
          Loading Takeoff plan documents...
        </div>
      ) : (
        <div className="space-y-1.5">
          {takeoffOptions.planDocuments.map((plan) => {
            const key = String(plan.sourcePlanDocumentId);
            const selected = takeoffSelections[key];
            const changedRevisions = getNewerTakeoffRevisions(plan);
            const hasChanged = changedRevisions.length > 0;
            return (
              <div
                key={key}
                className="space-y-2 rounded-md border bg-background px-3 py-2"
              >
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={Boolean(selected)}
                    disabled={!hasChanged || isSubmitting}
                    onCheckedChange={(checked) => {
                      onSelectionsChange((current) => {
                        const next = { ...current };
                        if (checked !== true) {
                          delete next[key];
                          deselectedDocumentKeys.current.add(key);
                        } else {
                          deselectedDocumentKeys.current.delete(key);
                          next[key] = {
                            v1RevisionId: String(plan.currentRevisionId),
                            v2RevisionId: String(
                              changedRevisions.at(-1)?._id ?? ""
                            ),
                          };
                        }
                        return next;
                      });
                      onClearError();
                    }}
                  />
                  <FileText className="size-3.5 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 truncate text-sm font-medium">
                    {plan.documentName}
                  </span>
                  {!hasChanged ? (
                    <span className="ms-auto text-[10px] text-muted-foreground">
                      No newer revision
                    </span>
                  ) : null}
                </div>
                {selected ? (
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div className="flex h-7 items-center rounded-md border bg-muted/30 px-2 text-xs text-muted-foreground">
                      {plan.currentRevisionName} (r
                      {plan.currentRevisionNumber})
                    </div>
                    <Select
                      value={selected.v2RevisionId}
                      onValueChange={(value) => {
                        onSelectionsChange((current) => ({
                          ...current,
                          [key]: {
                            v1RevisionId: String(plan.currentRevisionId),
                            v2RevisionId: value,
                          },
                        }));
                        onClearError();
                      }}
                      disabled={!hasChanged || isSubmitting}
                    >
                      <SelectTrigger
                        aria-label={`Newer revision for ${plan.documentName}`}
                        className="h-7 text-xs"
                      >
                        <SelectValue placeholder="v2 (newer)" />
                      </SelectTrigger>
                      <SelectContent>
                        {changedRevisions.map((r) => (
                          <SelectItem
                            key={r._id}
                            value={String(r._id)}
                          >
                            {r.revisionName} (r{r.revisionNumber})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
